import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  Step,
  StepButton,
  Stepper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import {
  useCreateProgramMutation,
  useGetProgramQuery,
  useGetProgramFolderQuery,
  useGetTreatmentAreasQuery,
  useSaveTreatmentAreaMutation,
  useGetMeasurementDefinitionsQuery,
  useSaveMeasurementDefinitionMutation,
  useGetMasteryWorkflowQuery,
  useGetPromptTemplateQuery,
  useGetProgramTargetQuery,
} from "../../../Api/attoDeskApi";
import {
  useHistoricalTreatmentArea,
  useHistoricalMeasurementDefinition,
} from "../shared/useHistoricalRetiredOption";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { useNotifier } from "../../../Core/Notifier";
import FolderPicker from "../shared/FolderPicker";
import ProgramLockedBanner from "../shared/ProgramLockedBanner";
import QuickHierarchyDrawer from "../shared/QuickHierarchyDrawer";
import MaintenanceIntervalSubControl from "./MaintenanceIntervalSubControl";
import StudentsStaffWorkspace from "../shared/StudentsStaffWorkspace";
import TargetsWorkspace from "../shared/TargetsWorkspace";
import {
  PageSurface, LegacyTokens, TypeScale,
  resolveStatusTriad, programStatusLabel, PROGRAM_STATUS_LABELS,
  B13, B13Ink, resolveB13Status,
} from "../../../theme/b13Tokens";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import PanToolIcon from "@mui/icons-material/PanTool";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RepeatIcon from "@mui/icons-material/Repeat";
import PauseIcon from "@mui/icons-material/Pause";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";

/**
 * Batch 31 (Phase 1)  -  Program editor.
 *
 * Multi-section form covering the ABA spec:
 *   1. Overview
 *   2. Teaching Instructions
 *   3. Prompting
 *   4. Mastery & Data Collection
 *   5. Assignments (see: Copy-to-clients dialog from Batch 29 for the
 *      "assign to multiple students" flow; this section shows the
 *      current single owner + staff assignment, and links to the
 *      library-level copy dialog for multi-assign)
 *   6. Review
 *
 * Handles both /aba-therapy/programs/new and /aba-therapy/programs/edit/:id.
 * All fields are optional  -  a program can be saved as a draft with just
 * a name. Validation only blocks on invalid data, never on emptiness.
 *
 * Targets are edited on the Program Details page (Targets tab, Batch 30
 * work); this editor deliberately does not duplicate that surface.
 */
// Batch 62  - 3  -  CATEGORIES removed. Treatment Areas are now
// supervisor-managed via useGetTreatmentAreasQuery.
// Batch 15 Step 3c  -  local MEASUREMENTS array retired. Options come
// from the shared source at SessionData/shared/measurementTypeOptions.
// If a new type is added, add it once there  -  never here again.
import { MEASUREMENT_TYPE_OPTIONS, pickerValueFromRow, measurementTypeLabel } from "../../SessionData/shared/measurementTypeOptions";
import type { MeasurementType } from "../../SessionData/shared/measurementType";
// Batch 43  - 5  -  aligned to the seven-value program vocabulary. Older rows
// may still hold `draft`, `on_hold` or `archived`; the picker treats those
// as "closest match" (draft?waiting, on_hold?hold, archived?discontinued)
// via LEGACY_STATUS_ALIASES and normalises on next save. Nothing is
// backfilled  -  historical values keep rendering with their label until
// the user re-picks.
const STATUS_KEYS = ["waiting", "baseline", "active", "maintenance", "hold", "closed", "discontinued"] as const;
type StatusKey = typeof STATUS_KEYS[number];
const LEGACY_STATUS_ALIASES: Record<string, StatusKey> = {
  draft: "waiting",
  on_hold: "hold",
  archived: "discontinued",
};
// Batch 2  - 2.6  -  icons audited against spec table. Marginal matches
// left alone (Waiting=Hourglass, Baseline=Assessment); spec-explicit
// mismatches corrected: Active ? running figure (DirectionsRun),
// Hold ? raised hand (PanTool), Closed ? double checkmark (DoneAll).
const STATUS_ICONS: Record<StatusKey, React.ReactElement> = {
  waiting:      <HourglassEmptyIcon fontSize="small" />,
  baseline:     <AssessmentOutlinedIcon fontSize="small" />,
  active:       <DirectionsRunIcon fontSize="small" />,
  maintenance:  <RepeatIcon fontSize="small" />,
  hold:         <PanToolIcon fontSize="small" />,
  closed:       <DoneAllIcon fontSize="small" />,
  discontinued: <BlockIcon fontSize="small" />,
};
const PROMPTING_DIRECTIONS = [
  { value: "most-to-least", label: "Most-to-Least", desc: "Begin with the highest level of support and gradually fade prompts." },
  { value: "least-to-most", label: "Least-to-Most", desc: "Begin with the least intrusive prompt and increase support only when needed." },
  { value: "custom",        label: "Custom Order",  desc: "Create your own ordered prompt sequence  -  add Prompt Levels below and drag them into the exact order Staff should follow." },
];
// MAINTENANCE_FREQUENCIES constant removed with the duplicate <Select>
// (Batch 2  - 2 follow-up). The MaintenanceIntervalSubControl parser
// still round-trips these legacy string values on read.
const GRAPH_TYPES = [
  { value: "line", label: "Line" },
  { value: "bar", label: "Bar" },
  { value: "cumulative", label: "Cumulative" },
];

type Values = {
  programName: string;
  category: string;
  treatment_area_id: number | null;
  domain: string;
  skill_area: string;
  folderId: string | null;
  studentId: number | null; // single-student assignment; multi-assign uses Copy-to-clients
  status: string;
  description: string;
  tags: string[];
  tagInput: string;
  // teaching
  objective: string;
  operational_definition: string;
  sd_general: string;
  teaching_procedure: string;
  materials: string;
  expected_response: string;
  incorrect_response_handling: string;
  error_correction: string;
  reinforcement: string;
  generalization_notes: string;
  maintenance_notes: string;
  safety_notes: string;
  additional_notes: string;
  // prompting
  promptTemplateId: string | null;
  prompting_direction: string;
  default_prompt_level_id: string | null;
  prompt_delay_seconds: string;
  prompt_fading_rule: string;
  independent_response_definition: string;
  // mastery
  masteryWorkflowId: string | null;
  measurementType: string;                       // legacy label; retained for backward-compat during T29 window
  measurement_type: MeasurementType | null;      // Batch 15 Step 3c  -  canonical enum key; the payload authority
  // Hybrid measurement layer (2026-08-19). The dropdown NOW picks a
  // MeasurementDefinition (id-based); the backend mirrors that
  // definition's recorder_type onto `measurement_type` so downstream
  // Session Data recording keeps rendering the correct Recorder.
  measurement_definition_id: number | null;
  trials_per_session: string;
  minimum_sessions: string;
  mastery_threshold_percent: string;
  baseline_required: boolean;
  baselineNotes: string;
  maintenance_frequency: string;
  automatic_progression: boolean;
  manual_override: boolean;
  graph_type: string;
  // assignments
  start_date: string;
  review_date: string;
};

const emptyValues: Values = {
  programName: "", category: "", treatment_area_id: null, domain: "", skill_area: "", folderId: null,
  studentId: null, status: "waiting", description: "", tags: [], tagInput: "",
  objective: "", operational_definition: "", sd_general: "", teaching_procedure: "",
  materials: "", expected_response: "", incorrect_response_handling: "",
  error_correction: "", reinforcement: "", generalization_notes: "",
  maintenance_notes: "", safety_notes: "", additional_notes: "",
  promptTemplateId: null, prompting_direction: "", default_prompt_level_id: null,
  prompt_delay_seconds: "", prompt_fading_rule: "", independent_response_definition: "",
  masteryWorkflowId: null, measurementType: "", measurement_type: null, measurement_definition_id: null, trials_per_session: "",
  minimum_sessions: "", mastery_threshold_percent: "", baseline_required: false,
  baselineNotes: "", maintenance_frequency: "", automatic_progression: false,
  manual_override: true, graph_type: "",
  start_date: "", review_date: "",
};

const SECTIONS = [
  "Program Details",
  "Teaching Plan",
  "Prompting",
  "Data & Mastery",
  "Targets",
  "Students & Staff",
  "Review & Activate",
];

function ReviewActivatePanel({ values, templates, workflows, folderLabel, programId, onJump }: {
  values: any; templates: any[]; workflows: any[]; folderLabel: string;
  programId: number | null; onJump: (step: number) => void;
}) {
  const { data: targetsData } = useGetProgramTargetQuery(programId ? { programId } : { programId: 0 }, { skip: !programId });
  const targets: any[] = Array.isArray(targetsData?.data) ? (targetsData!.data as any[]) : [];
  const activeTargets = targets.filter((t) => !["archived", "closed", "discontinued", "hold", "on_hold"].includes(String(t.status || "").toLowerCase()));
  const promptTpl = templates.find((t: any) => String(t.id) === values.promptTemplateId);
  const workflow  = workflows.find((w: any) => String(w.id) === values.masteryWorkflowId);
  const teachingDone = !!(values.objective || values.teaching_procedure);
  const promptingDone = !!promptTpl;
  const masteryDone = !!workflow;

  const blocking: string[] = [];
  if (!values.programName.trim()) blocking.push("Program Name is required");
  if (!promptingDone) blocking.push("A Prompt Levels is required");
  if (!masteryDone) blocking.push("A Workflow is required");
  if (!activeTargets.length) blocking.push("At least one active Target is required");

  const warnings: string[] = [];
  if (!values.description) warnings.push("Program Summary is empty");
  if (!values.category && !values.treatment_area_id) warnings.push("Treatment Area is not set");
  if (!values.safety_notes) warnings.push("Safety Notes are empty");
  if (!values.review_date) warnings.push("Review Date is not set");

  const ready: string[] = [];
  if (values.programName.trim()) ready.push("Program Name is set");
  if (promptingDone) ready.push(`Prompt Levels: ${promptTpl.templateName}`);
  if (masteryDone)  ready.push(`Workflow: ${workflow.workflowName}`);
  if (teachingDone) ready.push("Teaching Plan captured");
  if (activeTargets.length) ready.push(`${activeTargets.length} active Target${activeTargets.length === 1 ? "" : "s"}`);

  // Aligned row for the blocking / warning / ready lists. CSS grid
  // (not flex) so the Fix column has a fixed width AT the same X in
  // every row — flex justify-space-between let text length change
  // where Fix landed. Grid guarantees column alignment.
  const Item = ({ children, jump }: { children: React.ReactNode; jump?: number }) => (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: jump != null ? "1fr auto" : "1fr",
      alignItems: "center",
      columnGap: 1,
      py: 0.75,
      borderBottom: "1px solid rgba(15,23,42,0.05)",
      "&:last-of-type": { borderBottom: "none" },
    }}>
      <Typography variant="caption" sx={{ fontWeight: 500, color: "text.primary", lineHeight: 1.4 }}>
        {children}
      </Typography>
      {jump != null && (
        <Button
          size="small"
          onClick={() => onJump(jump)}
          sx={{ minWidth: 0, px: 1, py: 0.2, textTransform: "none", fontWeight: 700, fontSize: 11.5 }}
        >
          Fix
        </Button>
      )}
    </Box>
  );

  // Shared card styling — NO fixed height, NO flex hacks on the outer
  // <Card>. Cards fill their MUI Grid item's column naturally because
  // block-level elements default to width:100% inside the item; height
  // is content-driven so short cards don't stamp a dead 168px zone.
  const cardBaseSx = {
    p: 2,
    borderRadius: 2,
    width: "100%",
  } as const;
  const cardTitleSx = {
    fontSize: 13,
    fontWeight: 800,
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    color: "text.secondary",
    mb: 1.25,
  };

  const reviewCards: Array<{ title: string; content: React.ReactNode }> = [
    {
      title: "Program Details",
      content: (
        <>
          <ReviewItem label="Program Name"    value={values.programName} />
          <ReviewItem label="Treatment Area"  value={values.category || " - "} />
          <ReviewItem label="Folder"          value={folderLabel} />
          <ReviewItem label="Clinical Domain" value={values.domain} />
          <ReviewItem label="Status"          value={programStatusLabel(values.status)} />
        </>
      ),
    },
    {
      title: "Prompting & Mastery",
      content: (
        <>
          <ReviewItem label="Prompt Levels"    value={promptTpl?.templateName ?? "Not set"} />
          <ReviewItem label="Prompting Order"  value={values.prompting_direction || "Not set"} />
          <ReviewItem label="Workflow"         value={workflow?.workflowName ?? "Not set"} />
          <ReviewItem label="Measurement Type" value={values.measurement_type ? measurementTypeLabel(values.measurement_type) : (values.measurementType || "Not set")} />
        </>
      ),
    },
    {
      title: "Teaching Plan",
      content: (
        <>
          <ReviewItem label="Objective"     value={values.objective ? "Captured" : "Empty"} />
          <ReviewItem label="Procedure"     value={values.teaching_procedure ? "Captured" : "Empty"} />
          <ReviewItem label="Reinforcement" value={values.reinforcement ? "Captured" : "Empty"} />
          <ReviewItem label="Safety Notes"  value={values.safety_notes ? "Captured" : "Empty"} />
        </>
      ),
    },
    {
      title: "Targets",
      content: (
        <>
          <ReviewItem label="Total Targets"  value={String(targets.length)} />
          <ReviewItem label="Active Targets" value={String(activeTargets.length)} />
          <ReviewItem label="Target Types"   value={Array.from(new Set(targets.map((t) => t.targetType || "Single Response"))).join(", ") || " - "} />
        </>
      ),
    },
  ];

  return (
    <>
      {/* Review & Activate — 2×2 on desktop. Plain MUI Grid item
          xs=12 md=6 pattern with no sx overrides on the item itself,
          so md={6} keeps its 50% column basis. Card fills that column
          because it's a block element with width:100% baked into
          cardBaseSx (belt + braces). */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Review &amp; Activate</Typography>
        <Grid container spacing={2}>
          {reviewCards.map((c) => (
            <Grid item xs={12} md={6} key={c.title}>
              <Card variant="outlined" sx={cardBaseSx}>
                <Typography sx={cardTitleSx}>{c.title}</Typography>
                {c.content}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Readiness — 3 equal columns on desktop. Same rule: plain
          xs=12 md=4 items, cards fill via width:100%, no forced
          minHeight so a mostly-empty Ready card doesn't leave a dead
          zone. */}
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Readiness</Typography>
        <Grid container spacing={2}>
          {[
            {
              key: "blocking", accent: "error.main" as const, count: blocking.length,
              title: `Blocking issues (${blocking.length})`,
              empty: "No blocking issues. Ready to activate.",
              paletteKey: "error" as const,
              items: blocking.map((b, i) => (
                <Item key={i} jump={b.includes("Prompt") ? 2 : b.includes("Mastery") ? 3 : b.includes("Target") ? 4 : 0}>{b}</Item>
              )),
            },
            {
              key: "warnings", accent: "warning.main" as const, count: warnings.length,
              title: `Warnings (${warnings.length})`,
              empty: "No warnings.",
              paletteKey: "warning" as const,
              items: warnings.map((w, i) => <Item key={i}>{w}</Item>),
            },
            {
              key: "ready", accent: "success.main" as const, count: ready.length,
              title: `Ready (${ready.length})`,
              empty: "Nothing captured yet.",
              paletteKey: "success" as const,
              items: ready.map((r, i) => <Item key={i}>{r}</Item>),
            },
          ].map((col) => (
            <Grid item xs={12} sm={6} md={4} key={col.key}>
              <Card
                variant="outlined"
                sx={{ ...cardBaseSx, borderLeft: (t) => `4px solid ${t.palette[col.paletteKey].main}` }}
              >
                <Typography sx={{ ...cardTitleSx, color: col.accent, mb: 1 }}>{col.title}</Typography>
                {col.count === 0 ? (
                  <Typography variant="caption" color="text.secondary">{col.empty}</Typography>
                ) : (
                  <Box>{col.items}</Box>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}

// Program Setup checklist row.
//   variant="check" (default) — activation-readiness item; renders a
//     green tick when `done`, an empty circle otherwise. Never green
//     unless THIS program has the requirement configured (no fall-back
//     to global lists such as "system has some workflows available").
//   variant="info" — informational item (e.g. "Students can be assigned
//     now or later"). Always neutral; never shown as a completed state
//     so a brand-new program can't accidentally look ready.
function SetupItem({
  done,
  label,
  variant = "check",
}: {
  done?: boolean;
  label: string;
  variant?: "check" | "info";
}) {
  if (variant === "info") {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <InfoOutlinedIcon fontSize="small" sx={{ color: "info.main" }} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>{label}</Typography>
      </Stack>
    );
  }
  const isDone = !!done;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {isDone
        ? <CheckCircleIcon fontSize="small" color="success" />
        : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
      <Typography variant="caption" sx={{ color: isDone ? "text.primary" : "text.secondary" }}>{label}</Typography>
    </Stack>
  );
}

export default function ProgramEditor() {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id?: string }>();
  const isEdit = !!routeId;
  const { showMessage, showErrorMessage } = useNotifier();

  const [saveProgram, { isLoading: saving }] = useCreateProgramMutation();
  // Batch 56  - 1a  -  set to true if the row's tags column couldn't be
  // hydrated (unreadable JSON). While true, submit omits the `tags`
  // key so the backend UPDATE preserves the existing DB value
  // instead of overwriting it with json_encode([]).
  const tagsHydrationFailedRef = useRef(false);
  const { data: folderData } = useGetProgramFolderQuery({});
  const { data: treatmentAreasData, refetch: refetchTreatmentAreas } = useGetTreatmentAreasQuery();
  const [saveTreatmentArea, { isLoading: creatingTreatmentArea }] = useSaveTreatmentAreaMutation();
  // Inline "+ Create new treatment area" dialog state. Opened from
  // either the Treatment Area Select's create-new sentinel option OR
  // the empty-state banner. Kept local to the editor so the Settings
  // page remains the authoritative CRUD surface.
  const [newAreaOpen, setNewAreaOpen] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");

  // Measurement Definitions — hybrid layer over the closed 7 recorder
  // enum. Dropdown shows definition NAMES; on save the Program stores
  // measurement_definition_id and the backend mirrors the definition's
  // recorder_type onto the legacy `measurement_type` enum column so
  // Session Data recording keeps rendering the correct Recorder.
  const { data: measurementDefsData, refetch: refetchMeasurementDefs } = useGetMeasurementDefinitionsQuery();
  const measurementDefs: Array<{ id: number; name: string; recorder_type: string; active: boolean; is_built_in: boolean }> =
    Array.isArray(measurementDefsData?.data) ? (measurementDefsData!.data as any[]) : [];
  const [saveMeasurementDefinition, { isLoading: creatingMeasurement }] = useSaveMeasurementDefinitionMutation();
  const [newMeasurementOpen, setNewMeasurementOpen] = useState(false);
  const [newMeasurementName, setNewMeasurementName] = useState("");
  const [newMeasurementRecorder, setNewMeasurementRecorder] = useState<string>("");
  const treatmentAreas: Array<{ id: number; name: string; is_active: number }> =
    Array.isArray(treatmentAreasData?.data) ? (treatmentAreasData!.data as any[]) : [];
  const { data: workflowData, refetch: refetchWorkflows } = useGetMasteryWorkflowQuery({});
  const { data: templateData, refetch: refetchTemplates } = useGetPromptTemplateQuery({});
  const [quickDrawer, setQuickDrawer] = useState<null | "hierarchy" | "workflow">(null);
  const { data: existingData, isLoading: loadingExisting } = useGetProgramQuery(
    isEdit ? { id: routeId } : { searchText: "__skip__" },
    { skip: !isEdit }
  );

  // Program-scoped target list for the Setup checklist below. NEW
  // programs skip the fetch (no id yet) so "targets" stays empty and
  // the checklist row correctly shows incomplete. Same query the
  // Review & Activate panel uses — one source of truth.
  const { data: checklistTargetsData } = useGetProgramTargetQuery(
    isEdit ? { programId: Number(routeId) } : { programId: 0 },
    { skip: !isEdit }
  );
  const checklistActiveTargetCount = React.useMemo(() => {
    const rows = Array.isArray(checklistTargetsData?.data) ? (checklistTargetsData!.data as any[]) : [];
    return rows.filter((t) =>
      !["archived", "closed", "discontinued", "hold", "on_hold"].includes(String(t.status || "").toLowerCase())
    ).length;
  }, [checklistTargetsData]);

  // Derived from the payload the editor already fetches. The backend
  // `TREE_PROJECTION_INCLUDED` set carries `isLocked` on every program row
  // (ProgramController.php:924-975), coerced to bool at :1932. When the
  // banner's Unlock mutation resolves, RTK invalidates the "program" tag,
  // this query auto-refetches, and `programIsLocked` flips to false — the
  // fieldset re-enables and the Save button reappears without a reload.
  const existingProgram = useMemo(() => {
    const arr = Array.isArray(existingData?.data) ? (existingData!.data as any[]) : [];
    return arr[0] ?? null;
  }, [existingData]);
  const programIsLocked = isEdit && !!existingProgram?.isLocked;

  const folders = useMemo(
    () => (Array.isArray(folderData?.data) ? (folderData!.data as any[]) : []),
    [folderData]
  );
  const workflows = useMemo(
    () => (Array.isArray(workflowData?.data) ? (workflowData!.data as any[]) : []),
    [workflowData]
  );
  const templates = useMemo(
    () => (Array.isArray(templateData?.data) ? (templateData!.data as any[]) : []),
    [templateData]
  );

  // Flatten folder tree for the picker (nested folders show indented).
  const flatFolders = useMemo(() => {
    const out: Array<{ id: string; label: string; depth: number }> = [];
    const walk = (list: any[], depth: number) => {
      for (const f of list ?? []) {
        out.push({ id: String(f.id), label: f.folderName, depth });
        if (f.children?.length) walk(f.children, depth + 1);
      }
    };
    walk(folders, 0);
    return out;
  }, [folders]);

  // Levels for the selected prompt template  -  used to render the default
  // prompt level picker.
  const [activeSection, setActiveSection] = useState(0);
  // Batch 66  - 6  -  tab-shell state and helpers are declared AFTER the
  // formik useFormik() call below so handleTabClick / discardAndSwitch
  // can close over formik. See "Batch 66  - 6  -  Tab shell" block after
  // formik.

  const formik = useFormik<Values>({
    initialValues: emptyValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.programName.trim()) {
        setActiveSection(0);
        showErrorMessage("Program Name is required.");
        // Same pattern as Mastery Workflow below: scroll the field into
        // view and focus the input so the user sees exactly which field
        // failed. Replaces reliance on the browser's native constraint-
        // validation bubble (suppressed by <form noValidate>).
        setTimeout(() => {
          const node = document.querySelector<HTMLElement>('[data-testid="program-name-field"]');
          if (node) {
            node.scrollIntoView({ behavior: "smooth", block: "center" });
            const input = node.querySelector<HTMLInputElement>("input");
            input?.focus();
          }
        }, 0);
        return;
      }
      // Batch 2  - 2.3  -  Mastery Workflow is required. Blocks save on
      // EVERY status, not just Active  -  a program without a workflow
      // cannot progress targets and shouldn't be persisted even as
      // a draft. The inline field error renders via
      // formik.submitCount > 0 && !values.masteryWorkflowId. Per  - 1.1
      // ack: a toast on a long stepper page is not enough feedback  - 
      // scroll the field into view and focus it so the user sees
      // exactly which field failed.
      if (!values.masteryWorkflowId) {
        setActiveSection(0);
        showErrorMessage("Please choose a Mastery Workflow.");
        // Give the section-switch a tick to render before scrolling.
        setTimeout(() => {
          const node = document.querySelector<HTMLElement>('[data-testid="mastery-workflow-field"]');
          if (node) {
            node.scrollIntoView({ behavior: "smooth", block: "center" });
            const input = node.querySelector<HTMLElement>('[role="combobox"], input, [tabindex="0"]');
            input?.focus();
          }
        }, 0);
        return;
      }
      // Batch 32  -  Save-and-Activate requires prereqs. Save-as-Draft doesn't.
      if (values.status === "active") {
        if (templates.length === 0) {
          showErrorMessage("Cannot activate: no Prompt Levels exist. Create one first from Settings > Prompt Levels.");
          return;
        }
        if (workflows.length === 0) {
          showErrorMessage("Cannot activate: no Workflows exist. Create one from Settings > Workflows or from the Workflow field below.");
          return;
        }
      }
      const payload: Record<string, unknown> = {
        programName: values.programName.trim(),
        category: values.category || null,
        // Batch 61  - 2  -  send the FK; backend mirrors name into category.
        treatment_area_id: values.treatment_area_id ?? null,
        domain: values.domain || null,
        skill_area: values.skill_area || null,
        folderId: values.folderId || null,
        studentId: values.studentId || null,
        status: values.status || "waiting",
        description: values.description || null,
        // Batch 56  - 1a  -  omit tags entirely if hydration failed on
        // load. The controller preserves omitted keys; sending
        // json_encode([]) would clobber the intact DB value.
        ...(tagsHydrationFailedRef.current ? {} : { tags: values.tags }),
        objective: values.objective || null,
        operational_definition: values.operational_definition || null,
        sd_general: values.sd_general || null,
        teaching_procedure: values.teaching_procedure || null,
        materials: values.materials || null,
        expected_response: values.expected_response || null,
        incorrect_response_handling: values.incorrect_response_handling || null,
        error_correction: values.error_correction || null,
        reinforcement: values.reinforcement || null,
        generalization_notes: values.generalization_notes || null,
        maintenance_notes: values.maintenance_notes || null,
        safety_notes: values.safety_notes || null,
        additional_notes: values.additional_notes || null,
        promptTemplateId: values.promptTemplateId || null,
        prompting_direction: values.prompting_direction || null,
        default_prompt_level_id: values.default_prompt_level_id || null,
        prompt_delay_seconds: values.prompt_delay_seconds ? Number(values.prompt_delay_seconds) : null,
        prompt_fading_rule: values.prompt_fading_rule || null,
        independent_response_definition: values.independent_response_definition || null,
        masteryWorkflowId: values.masteryWorkflowId || null,
        // Batch 15 Step 3c  -  send the enum key as the authority. The
        // backend reconciler derives the legacy varchar label from
        // LABEL_BY_ENUM and writes both columns. Legacy
        // `measurementType` field omitted to avoid a same-request
        // conflict-warning log when the two would agree; the backend
        // still accepts it from old clients.
        measurement_type: values.measurement_type ?? null,
        // Hybrid layer — the id is the authority. Backend controller
        // mirrors the definition's recorder_type onto measurement_type
        // automatically, so old clients that only send measurement_type
        // (no id) still work identically to before.
        measurement_definition_id: values.measurement_definition_id ?? null,
        trials_per_session: values.trials_per_session ? Number(values.trials_per_session) : null,
        minimum_sessions: values.minimum_sessions ? Number(values.minimum_sessions) : null,
        mastery_threshold_percent: values.mastery_threshold_percent
          ? Number(values.mastery_threshold_percent) : null,
        baseline_required: values.baseline_required,
        baselineNotes: values.baselineNotes || null,
        maintenance_frequency: values.maintenance_frequency || null,
        automatic_progression: values.automatic_progression,
        manual_override: values.manual_override,
        graph_type: values.graph_type || null,
        start_date: values.start_date || null,
        review_date: values.review_date || null,
      };
      if (isEdit) (payload as any).id = Number(routeId);
      try {
        const res = await saveProgram(payload).unwrap();
        if (!res.status) { showErrorMessage(res.message || "Save failed."); return; }
        showMessage(res.message || (isEdit ? "Program updated." : "Program created."));
        const newId = (res.data as any)?.id ?? routeId;
        navigate(`/aba-therapy/programs/${newId}`);
      } catch (e: any) {
        showErrorMessage(e?.data?.message || e?.message || "Save failed.");
      }
    },
  });

  // ----------------------------------------------------------------
  // Batch 66  - 6  -  Tab shell over the 7-section stepper.
  //
  // Four tabs render 1-3 sections each. `activeSection` stays as the
  // internal source of truth so all existing section JSX blocks keep
  // rendering unchanged; a `visibleSections` set decides which get
  // shown at a time. The stepper is replaced by <Tabs> but the
  // per-section Cards below are otherwise untouched.
  //
  // Tab state precedence: ?tab= URL param > localStorage (per-id key) >
  // "treatment" default.
  //
  // Unsaved-changes guard: on tab-switch attempt, if formik.dirty,
  // show a two-option dialog (Discard / Cancel). No "Save and
  // continue"  -  a partial write on an unvalidated program leaves it
  // in an unanswerable state.
  // ----------------------------------------------------------------
  type TabKey = "treatment" | "instructions" | "targets" | "data";
  const TAB_ORDER: TabKey[] = ["treatment", "instructions", "targets", "data"];
  const TAB_LABEL: Record<TabKey, string> = {
    treatment: "Treatment",
    instructions: "Instructions",
    targets: "Targets",
    data: "Data Recording",
  };
  const TAB_TO_SECTIONS: Record<TabKey, number[]> = {
    treatment:    [0, 2, 3],
    instructions: [1],
    targets:      [4],
    data:         [5, 6],
  };
  const TAB_FIELDS: Record<TabKey, string[]> = {
    treatment:    ["programName", "treatment_area_id", "measurementType", "masteryWorkflowId", "objective", "baselineNotes", "promptTemplateId"],
    instructions: ["operational_definition", "teaching_procedure"],
    targets:      [],
    data:         ["trials_per_session", "minimum_sessions", "mastery_threshold_percent"],
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const tabStorageKey = `programWizardTab:${routeId ?? "new"}`;
  const readInitialTab = (): TabKey => {
    const urlTab = searchParams.get("tab");
    if (urlTab && TAB_ORDER.includes(urlTab as TabKey)) return urlTab as TabKey;
    try {
      const stored = localStorage.getItem(tabStorageKey);
      if (stored && TAB_ORDER.includes(stored as TabKey)) return stored as TabKey;
    } catch { /* ignore */ }
    return "treatment";
  };
  const [activeTab, setActiveTabState] = useState<TabKey>(readInitialTab);
  const setActiveTab = (t: TabKey, opts?: { keepSection?: boolean }) => {
    setActiveTabState(t);
    try { localStorage.setItem(tabStorageKey, t); } catch { /* ignore */ }
    const params = new URLSearchParams(searchParams);
    if (t === "treatment") params.delete("tab"); else params.set("tab", t);
    setSearchParams(params, { replace: true });
    if (!opts?.keepSection) setActiveSection(TAB_TO_SECTIONS[t][0]);
  };
  const isSectionVisible = (n: number): boolean => TAB_TO_SECTIONS[activeTab].includes(n);

  const [pendingTab, setPendingTab] = useState<TabKey | null>(null);
  const handleTabClick = (target: TabKey) => {
    if (target === activeTab) return;
    if (formik.dirty) { setPendingTab(target); return; }
    setActiveTab(target);
  };
  const discardAndSwitch = () => {
    if (pendingTab) { formik.resetForm(); setActiveTab(pendingTab); }
    setPendingTab(null);
  };
  const cancelSwitch = () => setPendingTab(null);

  // T63 Part E — historical retired-option hydration. Backend hides
  // inactive Treatment Areas + Measurement Definitions from the
  // normal picker feed. If the program being edited references a
  // retired row, resolve THAT row (only) via the include_inactive
  // fallback so the dropdown shows the current value tagged
  // "(Inactive)" instead of blank. Hooks internally skip their RTK
  // fetch when the current id is already in the active list, so this
  // is free for the common "no retired reference" case.
  const historicalTreatmentArea = useHistoricalTreatmentArea(
    formik.values.treatment_area_id,
    treatmentAreas,
  );
  const historicalMeasurementDef = useHistoricalMeasurementDefinition(
    formik.values.measurement_definition_id,
    measurementDefs,
  );

  // T63 Part I §2 — consume `isDefault` on Prompt Level Template +
  // Mastery Workflow when the user opens NEW Program create.
  // Previously the "Use as default template when adding new programs"
  // checkbox was written to the DB but no reader existed, so a fresh
  // Program editor started with both selectors on "Not set".
  //
  // Rules:
  //   • only fires on the CREATE path (not edit) so an existing
  //     program's stored selection is never overwritten;
  //   • only sets when the field is still empty — a user who has
  //     already picked something wins;
  //   • only picks ACTIVE / non-archived templates + workflows;
  //   • is idempotent (formik.setFieldValue is a no-op if value is
  //     already what we're setting).
  React.useEffect(() => {
    if (isEdit) return;
    if (!formik.values.promptTemplateId && templates.length > 0) {
      const def = templates.find((t: any) =>
        (t.isDefault === true || Number(t.isDefault) === 1) &&
        String(t.status ?? "active") !== "archived" &&
        Number(t.isDelete ?? 0) === 0
      );
      if (def) formik.setFieldValue("promptTemplateId", String(def.id));
    }
    if (!formik.values.masteryWorkflowId && workflows.length > 0) {
      const def = workflows.find((w: any) =>
        (w.isDefault === true || Number(w.isDefault) === 1) &&
        String(w.status ?? "active") !== "archived" &&
        Number(w.isDelete ?? 0) === 0
      );
      if (def) formik.setFieldValue("masteryWorkflowId", String(def.id));
    }
    // Only re-run when the list arrivals change — do NOT depend on
    // formik.values here or the effect fires on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, templates.length, workflows.length]);

  // Compute per-tab error count so the tab strip can show a red dot on
  // any tab with a save-blocking issue. Only counts required-field
  // emptiness for now  -  enough to steer the operator to the right tab.
  const tabErrorCount = React.useMemo((): Record<TabKey, number> => {
    const out: Record<TabKey, number> = { treatment: 0, instructions: 0, targets: 0, data: 0 };
    if (!(formik.submitCount > 0)) return out;
    // Program Name is the only hard-required field today.
    if (!formik.values.programName.trim()) out.treatment++;
    return out;
  }, [formik.submitCount, formik.values.programName]);

  // Hydrate from server row on Edit.
  useEffect(() => {
    if (!isEdit) return;
    const row = Array.isArray(existingData?.data)
      ? (existingData!.data as any[])[0]
      : (existingData?.data as any);
    if (!row) return;
    formik.resetForm({
      values: {
        ...emptyValues,
        programName: row.programName ?? "",
        category: row.category ?? "",
        treatment_area_id: row.treatment_area_id != null ? Number(row.treatment_area_id) : null,
        domain: row.domain ?? "",
        skill_area: row.skill_area ?? "",
        folderId: row.folderId != null ? String(row.folderId) : null,
        studentId: row.studentId != null ? Number(row.studentId) : null,
        status: row.status ?? "waiting",
        description: row.description ?? "",
        tags: (() => {
          // Batch 14 Step 3b  -  hydrate the editor's tag list from the
          // authoritative pivot names (row.tagsWithColour) rather than
          // the legacy JSON string. A rename in Settings now flows into
          // the Editor on open. The chip list is still bound to
          // formik.values.tags as an editable string[] so the mid-edit
          // UX is unchanged; the pivot only supplies the STARTING
          // values. Save resolves each string through the normalising
          // write path (Batch 14 Step 2 syncProgramTagsToPivot), so
          // freeform edits still land case-insensitively on the right
          // tags row.
          if (Array.isArray((row as any).tagsWithColour) && (row as any).tagsWithColour.length > 0) {
            return ((row as any).tagsWithColour as Array<{ name: string }>)
              .map((t) => t.name)
              .filter((n): n is string => typeof n === "string" && n.length > 0);
          }
          // Legacy fallback (Batch 56  - 1a)  -  reads the JSON column when
          // the endpoint hasn't populated tagsWithColour yet. Parse
          // failure MUST NOT silently return []; flag hydration so
          // submit omits the key rather than clobbering the DB.
          if (Array.isArray(row.tags)) return row.tags as string[];
          if (row.tags == null || row.tags === "") return [];
          if (typeof row.tags === "string") {
            try {
              const p = JSON.parse(row.tags);
              if (Array.isArray(p)) return p;
            } catch { /* fallthrough  -  hydration failed */ }
            tagsHydrationFailedRef.current = true;
            // eslint-disable-next-line no-console
            console.warn("[ProgramForm] tags column could not be parsed as JSON array; omitting from save payload to avoid clobbering the DB value.", row.tags);
            return [];
          }
          tagsHydrationFailedRef.current = true;
          return [];
        })(),
        tagInput: "",
        objective: row.objective ?? "",
        operational_definition: row.operational_definition ?? "",
        sd_general: row.sd_general ?? "",
        teaching_procedure: row.teaching_procedure ?? "",
        materials: row.materials ?? "",
        expected_response: row.expected_response ?? "",
        incorrect_response_handling: row.incorrect_response_handling ?? "",
        error_correction: row.error_correction ?? "",
        reinforcement: row.reinforcement ?? "",
        generalization_notes: row.generalization_notes ?? "",
        maintenance_notes: row.maintenance_notes ?? "",
        safety_notes: row.safety_notes ?? "",
        additional_notes: row.additional_notes ?? "",
        promptTemplateId: row.promptTemplateId != null ? String(row.promptTemplateId) : null,
        prompting_direction: row.prompting_direction ?? "",
        default_prompt_level_id: row.default_prompt_level_id != null ? String(row.default_prompt_level_id) : null,
        prompt_delay_seconds: row.prompt_delay_seconds != null ? String(row.prompt_delay_seconds) : "",
        prompt_fading_rule: row.prompt_fading_rule ?? "",
        independent_response_definition: row.independent_response_definition ?? "",
        masteryWorkflowId: row.masteryWorkflowId != null ? String(row.masteryWorkflowId) : null,
        measurementType: row.measurementType ?? "",
        // Batch 15 Step 3c  -  hydrate the picker's enum-key value. Prefers
        // the row's enum column; falls back through pickerValueFromRow's
        // legacy-varchar resolution when a row was last saved before Step 3c.
        measurement_type: (pickerValueFromRow({
          measurement_type: (row as any).measurement_type ?? null,
          measurementType: row.measurementType ?? null,
        }) || null) as MeasurementType | null,
        // Hybrid layer — if the row already has a definition id (from
        // the additive migration's backfill or a new save), hydrate it
        // so the dropdown shows the correct definition name. Otherwise
        // resolved by the effect below once definitions load.
        measurement_definition_id: (row as any).measurement_definition_id != null
          ? Number((row as any).measurement_definition_id)
          : null,
        trials_per_session: row.trials_per_session != null ? String(row.trials_per_session) : "",
        minimum_sessions: row.minimum_sessions != null ? String(row.minimum_sessions) : "",
        mastery_threshold_percent: row.mastery_threshold_percent != null ? String(row.mastery_threshold_percent) : "",
        baseline_required: !!row.baseline_required,
        baselineNotes: row.baselineNotes ?? "",
        maintenance_frequency: row.maintenance_frequency ?? "",
        automatic_progression: !!row.automatic_progression,
        manual_override: row.manual_override === undefined ? true : !!row.manual_override,
        graph_type: row.graph_type ?? "",
        start_date: row.start_date ?? "",
        review_date: row.review_date ?? "",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingData]);

  const missingFields: string[] = useMemo(() => {
    const out: string[] = [];
    if (!formik.values.programName.trim()) out.push("Program Name");
    return out;
  }, [formik.values.programName]);

  const promptLevels = useMemo(() => {
    const t = templates.find((x: any) => String(x.id) === String(formik.values.promptTemplateId));
    const levels = t?.levels ?? [];
    return Array.isArray(levels) ? levels : [];
  }, [templates, formik.values.promptTemplateId]);

  const handleAddTag = () => {
    const t = formik.values.tagInput.trim();
    if (!t) return;
    if (formik.values.tags.includes(t)) { formik.setFieldValue("tagInput", ""); return; }
    formik.setFieldValue("tags", [...formik.values.tags, t]);
    formik.setFieldValue("tagInput", "");
  };
  const handleRemoveTag = (tag: string) =>
    formik.setFieldValue("tags", formik.values.tags.filter((t) => t !== tag));

  if (isEdit && loadingExisting) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ ...PageSurface, p: { xs: 1.5, md: 2 } }}>
      {/* Breadcrumbs  -  Batch 32  - 1.3: ABA Therapy / Programs / <action> */}
      <Breadcrumbs sx={{ mb: 1 }} separator="/">
        <MuiLink component={Link} to="/aba-therapy/programs" underline="hover">ABA Therapy</MuiLink>
        <MuiLink component={Link} to="/aba-therapy/programs" underline="hover">Programs</MuiLink>
        {isEdit && (
          <MuiLink component={Link} to={`/aba-therapy/programs/${routeId}`} underline="hover">
            {formik.values.programName || `Program #${routeId}`}
          </MuiLink>
        )}
        <Typography color="text.primary">{isEdit ? "Edit" : "New Program"}</Typography>
      </Breadcrumbs>

      <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/aba-therapy/programs")} sx={{ textTransform: "none" }}>
            Back
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {isEdit ? "Edit program" : "New program"}
            </Typography>
            {/* Batch 2  -  draft language removed. Status controls whether a
               program is Active; there is no separate publish gesture. */}
          </Box>
        </Stack>
      </Card>

      {/* Program-locked banner. Rendered above the tabs so the user sees
         the state before touching any control. The RTK invalidation on
         unlock refetches the program payload, which flips programIsLocked
         back to false — the fieldset re-enables and Save reappears
         without a reload. */}
      {programIsLocked && (
        <ProgramLockedBanner
          programId={routeId as string | number}
          programName={existingProgram?.programName ?? formik.values.programName}
          disabledSurfaceLabel="editing this program"
        />
      )}

      {/* Batch 66  - 6  -  Four-tab strip replacing the seven-section
         Stepper. Each tab renders 1-3 of the original sections
         (see TAB_TO_SECTIONS map). A red dot on the tab label
         signals validation errors on that tab, so save-time failures
         point somewhere rather than surfacing as a silent floating
         banner. Tab click is guarded by formik.dirty  -  see
         handleTabClick + the Discard/Cancel dialog at bottom of file. */}
      <Card variant="outlined" sx={{ p: 0, mb: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => handleTabClick(v as TabKey)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          {TAB_ORDER.map((t) => (
            <Tab
              key={t}
              value={t}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{TAB_LABEL[t]}</span>
                  {tabErrorCount[t] > 0 && (
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: "error.main" }} />
                  )}
                </Stack>
              }
            />
          ))}
        </Tabs>
      </Card>

      {formik.submitCount > 0 && missingFields.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Fix the following before saving: <strong>{missingFields.join(", ")}</strong>.
        </Alert>
      )}

      {/* Batch 36  -  compact setup checklist. Live-updates as fields fill in.
          Batch 13 A.3  -  top-right "Create Prompt Levels" / "Create Workflow"
          pair removed. The same actions live inline next to their Select
          controls in the Data Recording tab; two copies confused users. */}
      <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>Program Setup</Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {/*
              Program-scoped completion checks — never fall back to
              "system has some workflows/templates available." A brand-
              new unsaved program starts with everything unchecked.
            */}
            <SetupItem done={formik.values.programName.trim().length > 0} label="Program Details" />
            <SetupItem done={!!formik.values.promptTemplateId} label="Prompt Levels required before activation" />
            <SetupItem done={!!formik.values.masteryWorkflowId} label="Workflow required before activation" />
            <SetupItem done={checklistActiveTargetCount > 0} label="At least one Target required before activation (edit Targets on the Program page)" />
            {/*
              Student assignment is optional at activation time (backend
              ProgramController::updateStatus does not require it), so
              this row is informational, not a checkable requirement.
              Rendering it as a permanent green tick on a brand-new
              program was misleading; a neutral info icon is honest.
            */}
            <SetupItem variant="info" label="Students can be assigned now or later" />
          </Stack>
        </Box>
      </Card>

      <QuickHierarchyDrawer
        open={quickDrawer !== null}
        kind={quickDrawer ?? "hierarchy"}
        onClose={() => setQuickDrawer(null)}
        onCreated={async (kind, id) => {
          if (kind === "hierarchy") {
            await refetchTemplates();
            formik.setFieldValue("promptTemplateId", String(id));
          } else {
            await refetchWorkflows();
            formik.setFieldValue("masteryWorkflowId", String(id));
          }
        }}
      />

      {/* Inline "+ Create new treatment area" dialog. Reuses the
          existing /aba/treatment-areas save mutation (same one the
          Settings > Treatment Areas page uses), refetches the list,
          then selects the freshly-created area in the Treatment Area
          field so the user's flow isn't interrupted. */}
      <Dialog
        open={newAreaOpen}
        onClose={() => (creatingTreatmentArea ? undefined : setNewAreaOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>New Treatment Area</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            placeholder="e.g. Communication"
            value={newAreaName}
            onChange={(e) => setNewAreaName(e.target.value)}
            disabled={creatingTreatmentArea}
            onKeyDown={async (e) => {
              if (e.key === "Enter" && newAreaName.trim() && !creatingTreatmentArea) {
                e.preventDefault();
                (document.getElementById("__create_treatment_area_save__") as HTMLButtonElement | null)?.click();
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewAreaOpen(false)} disabled={creatingTreatmentArea}>Cancel</Button>
          <Button
            id="__create_treatment_area_save__"
            variant="contained"
            disabled={!newAreaName.trim() || creatingTreatmentArea}
            onClick={async () => {
              const name = newAreaName.trim();
              if (!name) return;
              try {
                const res: any = await saveTreatmentArea({
                  id: null,
                  name,
                  description: null,
                  color: null,
                  order_number: treatmentAreas.length,
                  is_active: 1,
                }).unwrap();
                if (!res?.status) {
                  showErrorMessage(res?.message || "Failed to create treatment area.");
                  return;
                }
                await refetchTreatmentAreas();
                // Backend returns the created row; select it immediately.
                const createdId = Number(res?.data?.id);
                if (createdId) {
                  formik.setFieldValue("treatment_area_id", createdId);
                  formik.setFieldValue("category", name);
                }
                showMessage(res.message || "Treatment area created.");
                setNewAreaOpen(false);
                setNewAreaName("");
              } catch (e: any) {
                showErrorMessage(e?.data?.message || e?.message || "Failed to create treatment area.");
              }
            }}
          >
            {creatingTreatmentArea ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline "+ Create New Measurement" dialog. Custom label + one
          of the 7 base recorders — the recorder is what the Session
          Data pipeline uses at run-time; the label is what admins see
          in the Measurement dropdown. Refetches the definitions list on
          success and auto-selects the new one in the Program form so
          the admin's flow isn't interrupted. */}
      <Dialog
        open={newMeasurementOpen}
        onClose={() => (creatingMeasurement ? undefined : setNewMeasurementOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create New Measurement</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Measurement Name"
            placeholder="e.g. Percentage Correct"
            value={newMeasurementName}
            onChange={(e) => setNewMeasurementName(e.target.value)}
            disabled={creatingMeasurement}
            inputProps={{ maxLength: 120 }}
            sx={{ mt: 1 }}
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Base Recording Method</InputLabel>
            <Select
              label="Base Recording Method"
              value={newMeasurementRecorder}
              onChange={(e) => setNewMeasurementRecorder(String(e.target.value))}
              disabled={creatingMeasurement}
            >
              <MenuItem value="" disabled>Select a recorder…</MenuItem>
              {MEASUREMENT_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 1.25 }}>
            Choose how data for this measurement will be recorded during sessions. The custom name will appear in the Measurement dropdown; sessions will use the selected recorder's existing UI.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewMeasurementOpen(false)} disabled={creatingMeasurement}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newMeasurementName.trim() || !newMeasurementRecorder || creatingMeasurement}
            onClick={async () => {
              const name = newMeasurementName.trim();
              const recorder = newMeasurementRecorder;
              if (!name || !recorder) return;
              try {
                const res: any = await saveMeasurementDefinition({ name, recorder_type: recorder }).unwrap();
                if (!res?.status) {
                  showErrorMessage(res?.message || "Failed to create measurement.");
                  return;
                }
                await refetchMeasurementDefs();
                const createdId = Number(res?.data?.id);
                if (createdId) {
                  formik.setFieldValue("measurement_definition_id", createdId);
                  formik.setFieldValue("measurement_type", recorder);
                  formik.setFieldValue("measurementType", name);
                }
                showMessage(res.message || "Measurement created.");
                setNewMeasurementOpen(false);
                setNewMeasurementName("");
                setNewMeasurementRecorder("");
              } catch (e: any) {
                showErrorMessage(e?.data?.message || e?.message || "Failed to create measurement.");
              }
            }}
          >
            {creatingMeasurement ? "Creating…" : "Create Measurement"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* noValidate: suppress the browser's native constraint-validation
         bubble (the orange "Please fill out this field." popup). The MUI
         `required` prop stays on inputs for the label asterisk and
         accessibility; validation errors render inline via the existing
         `error` + `helperText` pattern (see Program Name at line 847 and
         Mastery Workflow at line 943). */}
      <form onSubmit={formik.handleSubmit} noValidate>
        {/* Native <fieldset disabled> cascades to every form-associated
           descendant (input, select, textarea, button) — one wrapper
           disables the whole edit surface without touching per-field
           props. The tabs sit OUTSIDE this form so tab navigation stays
           usable. Reset styling so the fieldset itself is invisible. */}
        <fieldset
          disabled={programIsLocked}
          style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
        >
        {/* ── Section 1: Overview ───────────────────────────────── */}
        {isSectionVisible(0) && (
          <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Overview</Typography>
            <Grid container spacing={2}>
              {/* Program Name  -  stays at the top (header treatment). */}
              <Grid item xs={12} md={8} data-testid="program-name-field">
                <TextField
                  fullWidth size="small" required label="Program Name"
                  value={formik.values.programName}
                  onChange={(e) => formik.setFieldValue("programName", e.target.value)}
                  error={formik.submitCount > 0 && !formik.values.programName.trim()}
                  helperText={formik.submitCount > 0 && !formik.values.programName.trim() ? "Required" : " "}
                />
              </Grid>

              {/* -- Batch 2  - 2.7 sequence --------------------------------
                 1. Treatment areas (empty-state banner OR select)
                 2. Measurement Type
                 3. Mastery Workflow (required + inline Create-new)
                 4. Objective (with blue ? help icon)
                 5. Baseline
                 6. Program Status (with Maintenance interval sub-control) */}

              {/*  - 2.1  -  Treatment Area empty-state banner OR select */}
              {treatmentAreas.length === 0 ? (
                <Grid item xs={12}>
                  <Box
                    data-testid="treatment-areas-empty-banner"
                    sx={{
                      p: 2.5,
                      borderRadius: 1.5,
                      backgroundColor: "#E8F0FE",
                      display: "flex", alignItems: "center", gap: 2,
                    }}
                  >
                    <Typography sx={{ flex: 1, fontSize: 13, color: B13Ink.ink2 }}>
                      You haven't set up any treatment areas. Set them up in Settings &gt; Programs &gt; Treatment Areas to group programs and simplify treatment planning and reporting.
                    </Typography>
                    <Button
                      component={Link}
                      to="/aba-therapy/settings/treatment-areas"
                      sx={{
                        textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
                        color: LegacyTokens.ink,
                      }}
                    >
                      Set up treatment areas
                    </Button>
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Treatment Area</InputLabel>
                    <Select
                      label="Treatment Area"
                      value={formik.values.treatment_area_id ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        // Sentinel matches the Mastery Workflow pattern below —
                        // opens the inline create dialog and does NOT persist
                        // the sentinel string into formik.
                        if (v === "__create_new__") {
                          setNewAreaName("");
                          setNewAreaOpen(true);
                          return;
                        }
                        const asId = v === "" ? null : Number(v);
                        formik.setFieldValue("treatment_area_id", asId);
                        const found = treatmentAreas.find((a) => a.id === asId);
                        formik.setFieldValue("category", found?.name ?? "");
                      }}
                    >
                      <MenuItem value="">Not set</MenuItem>
                      {treatmentAreas
                        .filter((a) => a.is_active)
                        .map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                      {/* Historical retired current value — rendered ONLY
                          when the program's stored id is missing from the
                          active feed. Selectable so the value can stay
                          selected across an unrelated field edit; once the
                          operator picks an active option the historical
                          entry disappears (see useHistoricalTreatmentArea
                          — its RTK fetch is skipped when the id lives in
                          the active list, so this branch collapses too). */}
                      {historicalTreatmentArea && (
                        <MenuItem
                          key={`historical-${historicalTreatmentArea.id}`}
                          value={historicalTreatmentArea.id}
                          sx={{ color: B13Ink.ink3, fontStyle: "italic" }}
                        >
                          {historicalTreatmentArea.name}{" "}
                          <Box component="span" sx={{
                            ml: 0.75, px: 0.75, py: 0.1, borderRadius: 999,
                            fontSize: 10, fontWeight: 700,
                            bgcolor: "#F1F5F9", color: "#64748B",
                            border: "1px solid #DCE5ED",
                          }}>
                            {historicalTreatmentArea.isKnown ? "Inactive" : "Unavailable"}
                          </Box>
                        </MenuItem>
                      )}
                      <Divider sx={{ my: 0.5 }} />
                      <MenuItem value="__create_new__" sx={{ color: B13.primary, fontWeight: 600 }}>
                        + Create new treatment area
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/*  - 2.2  -  Measurement Type. Batch 15 Step 3c  - 
                 options come from the shared MEASUREMENT_TYPE_OPTIONS
                 source (enum keys + display labels from
                 MEASUREMENT_LABELS). formik state now carries the
                 enum key; the payload field is `measurement_type` and
                 the controller writes both columns via the
                 reconciler. `measurementType` legacy label still
                 sent as null so an old client's hydration path
                 doesn't misfire. */}
              <Grid item xs={12} md={treatmentAreas.length === 0 ? 6 : 4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Measurement</InputLabel>
                  <Select
                    label="Measurement"
                    value={formik.values.measurement_definition_id ?? ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Sentinel matches the Treatment Area / Workflow
                      // inline-create pattern. Never persists the
                      // sentinel string into formik.
                      if (v === "__create_new__") {
                        setNewMeasurementName("");
                        setNewMeasurementRecorder("");
                        setNewMeasurementOpen(true);
                        return;
                      }
                      const asId = v === "" ? null : Number(v);
                      formik.setFieldValue("measurement_definition_id", asId);
                      // Also mirror the definition's recorder_type into the
                      // legacy `measurement_type` so any client-side reader
                      // (Review panel, Session Data preview) stays in sync
                      // without waiting for a round-trip.
                      const def = measurementDefs.find((d) => d.id === asId);
                      formik.setFieldValue("measurement_type", (def?.recorder_type as any) || null);
                      formik.setFieldValue("measurementType", def?.name || "");
                    }}
                  >
                    <MenuItem value="">Not set</MenuItem>
                    {measurementDefs
                      .filter((d) => d.active)
                      .map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                      ))}
                    {/* Historical retired current measurement — same
                        contract as the Treatment Area picker above.
                        Rendered only when the current id is missing
                        from the active list; hook's RTK fetch skips
                        otherwise. */}
                    {historicalMeasurementDef && (
                      <MenuItem
                        key={`historical-meas-${historicalMeasurementDef.id}`}
                        value={historicalMeasurementDef.id}
                        sx={{ color: B13Ink.ink3, fontStyle: "italic" }}
                      >
                        {historicalMeasurementDef.name}{" "}
                        <Box component="span" sx={{
                          ml: 0.75, px: 0.75, py: 0.1, borderRadius: 999,
                          fontSize: 10, fontWeight: 700,
                          bgcolor: "#F1F5F9", color: "#64748B",
                          border: "1px solid #DCE5ED",
                        }}>
                          {historicalMeasurementDef.isKnown ? "Inactive" : "Unavailable"}
                        </Box>
                      </MenuItem>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <MenuItem value="__create_new__" sx={{ color: B13.primary, fontWeight: 600 }}>
                      + Create New Measurement
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/*  - 2.3  -  Mastery Workflow: REQUIRED + inline "+ Create new
                 workflow" divider at the bottom of the dropdown. Blocks
                 SAVE when blank via required-field validation on the
                 stepper's submit path. */}
              <Grid item xs={12} md={treatmentAreas.length === 0 ? 6 : 4} data-testid="mastery-workflow-field">
                {(() => {
                  const wfEmpty = !formik.values.masteryWorkflowId;
                  const wfShowError = formik.submitCount > 0 && wfEmpty;
                  return (
                    <FormControl fullWidth size="small" error={wfShowError} required>
                      <InputLabel>Mastery Workflow</InputLabel>
                      <Select
                        label="Mastery Workflow"
                        value={formik.values.masteryWorkflowId ?? ""}
                        onChange={(e) => {
                          const v = e.target.value as string;
                          if (v === "__create_new__") {
                            setQuickDrawer("workflow");
                            return;
                          }
                          formik.setFieldValue("masteryWorkflowId", v || null);
                        }}
                      >
                        <MenuItem value="">Not set</MenuItem>
                        {workflows.map((w: any) => (
                          <MenuItem key={w.id} value={String(w.id)}>{w.workflowName}</MenuItem>
                        ))}
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem value="__create_new__" sx={{ color: B13.primary, fontWeight: 600 }}>
                          + Create new workflow
                        </MenuItem>
                      </Select>
                      {wfShowError && (
                        <Typography sx={{ fontSize: 11, color: "#D32F2F", mt: 0.5, ml: 1.5 }}>
                          Please choose a Mastery Workflow.
                        </Typography>
                      )}
                    </FormControl>
                  );
                })()}
              </Grid>

              {/*  - 2.4  -  Objective with blue circular ? help icon.
                 Tooltip copy PROVISIONAL pending clinical confirmation. */}
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2}
                  label="Objective"
                  value={formik.values.objective}
                  onChange={(e) => formik.setFieldValue("objective", e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <Tooltip arrow placement="left"
                        // Extra horizontal offset (distance component of
                        // the popper "offset" modifier) so the bubble
                        // clears the Objective field edge instead of
                        // overlapping it.
                        slotProps={{
                          popper: {
                            modifiers: [
                              { name: "offset", options: { offset: [0, 12] } },
                            ],
                          },
                        }}
                        title={
                          <React.Fragment>
                            <strong>Objective</strong><br />
                            What this program is teaching, written as an observable outcome. This describes the program as a whole; individual skills are defined separately as Targets.
                          </React.Fragment>
                        }>
                        <HelpOutlineIcon
                          fontSize="small"
                          sx={{
                            color: "#1A73E8",
                            cursor: "help",
                            alignSelf: "flex-start",
                            mt: 0.75, mr: 0.5,
                          }}
                        />
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/*  - 2.5  -  Baseline (already labelled correctly). */}
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2}
                  label="Baseline"
                  value={formik.values.baselineNotes}
                  onChange={(e) => formik.setFieldValue("baselineNotes", e.target.value)} />
              </Grid>

              {/*  - 2.6  -  Program Status row + Maintenance interval sub-
                 control (revealed only when status === "maintenance"). */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    ...TypeScale.fieldLabel, mb: 0.75, display: "block", color: B13Ink.ink3,
                  }}
                >
                  PROGRAM STATUS
                </Typography>
                <ProgramStatusPicker
                  value={formik.values.status}
                  onChange={(v) => formik.setFieldValue("status", v)}
                />
                {formik.values.status === "maintenance" && (
                  <MaintenanceIntervalSubControl
                    value={formik.values.maintenance_frequency}
                    onChange={(v) => formik.setFieldValue("maintenance_frequency", v)}
                  />
                )}
              </Grid>

              {/* -- Pre-existing "other" Overview fields  -  kept in place
                 (rule  - 4: never delete working sections; option (a)
                 from the pre-work approval). They live BELOW Batch 2's
                 six sequenced fields. */}
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Clinical Domain"
                  helperText="Broad area  -  Communication, Social Skills, Daily Living, Behaviour."
                  value={formik.values.domain}
                  onChange={(e) => formik.setFieldValue("domain", e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Skill Area / Focus"
                  helperText="Specific skill group within the selected Clinical Domain."
                  value={formik.values.skill_area}
                  onChange={(e) => formik.setFieldValue("skill_area", e.target.value)} />
              </Grid>
              <Grid item xs={12} md={4}>
                <FolderPicker
                  value={formik.values.folderId}
                  onChange={(id) => formik.setFieldValue("folderId", id)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={3} label="Program Summary"
                  helperText="A short family-friendly explanation of what this Program teaches."
                  value={formik.values.description}
                  onChange={(e) => formik.setFieldValue("description", e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {formik.values.tags.map((t) => (
                    <Chip key={t} label={t} size="small" onDelete={() => handleRemoveTag(t)} />
                  ))}
                  <TextField
                    size="small" label="Add tag" value={formik.values.tagInput}
                    onChange={(e) => formik.setFieldValue("tagInput", e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddTag(); } }}
                    sx={{ minWidth: 200 }}
                  />
                  <Button onClick={handleAddTag} size="small">Add</Button>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* ── Section 2: Teaching Instructions ─────────────────── */}
        {isSectionVisible(1) && (
          <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Teaching Instructions</Typography>
            <Grid container spacing={2}>
              {[
                // Batch 66  - 6  -  Objective moved to Treatment (Step 1). Removed
                // from Instructions to eliminate the duplicate field. Same
                // formik key, so pre-existing values still show on Treatment.
                // instructionsAll / instructionsProfessionals from Batch 114
                //  - 3 are NOT surfaced here per Batch 66 constraint  -  they
                // remain deferred regardless of the ghost's backend work.
                ["operational_definition", "Operational Definition"],
                ["sd_general", "Discriminative Stimulus (SD)"],
                ["teaching_procedure", "Teaching Procedure"],
                ["materials", "Materials"],
                ["expected_response", "Expected Response"],
                ["incorrect_response_handling", "Incorrect Response Handling"],
                ["error_correction", "Error Correction"],
                ["reinforcement", "Reinforcement"],
                ["generalization_notes", "Generalization"],
                ["maintenance_notes", "Maintenance"],
                ["safety_notes", "Safety Notes"],
                ["additional_notes", "Additional Notes"],
              ].map(([k, label]) => (
                <Grid key={k} item xs={12} md={6}>
                  <TextField
                    fullWidth size="small" multiline minRows={3} label={label}
                    value={(formik.values as any)[k] ?? ""}
                    onChange={(e) => formik.setFieldValue(k, e.target.value)}
                  />
                </Grid>
              ))}
            </Grid>
          </Card>
        )}

        {/* ── Section 3: Prompting ─────────────────────────────── */}
        {isSectionVisible(2) && (
          <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Prompting</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Prompt Levels</InputLabel>
                  <Select label="Prompt Levels" value={formik.values.promptTemplateId ?? ""}
                    onChange={(e) => {
                      formik.setFieldValue("promptTemplateId", e.target.value || null);
                      formik.setFieldValue("default_prompt_level_id", null);
                    }}>
                    <MenuItem value="">Not set</MenuItem>
                    {templates.map((t: any) => (
                      <MenuItem key={t.id} value={String(t.id)}>{t.templateName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" startIcon={<AutoFixHighIcon />} sx={{ mt: 0.5 }}
                  onClick={() => setQuickDrawer("hierarchy")}>
                  Create Prompt Levels
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Prompting Order</InputLabel>
                  <Select label="Prompting Order" value={formik.values.prompting_direction}
                    onChange={(e) => formik.setFieldValue("prompting_direction", e.target.value)}>
                    <MenuItem value="">Not set</MenuItem>
                    {PROMPTING_DIRECTIONS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                  </Select>
                </FormControl>
                {formik.values.prompting_direction && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {PROMPTING_DIRECTIONS.find((d) => d.value === formik.values.prompting_direction)?.desc}
                  </Typography>
                )}
              </Grid>
              {promptLevels.length > 0 && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Default Prompt Level</InputLabel>
                    <Select label="Default Prompt Level"
                      value={formik.values.default_prompt_level_id ?? ""}
                      onChange={(e) => formik.setFieldValue("default_prompt_level_id", e.target.value || null)}>
                      <MenuItem value="">Not set</MenuItem>
                      {promptLevels.map((lvl: any) => (
                        <MenuItem key={lvl.id} value={String(lvl.id)}>
                          {lvl.levelName ?? lvl.label} {lvl.score != null ? ` -  score ${lvl.score}` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" type="number" label="Prompt Delay (seconds)"
                  inputProps={{ min: 0, max: 120 }}
                  value={formik.values.prompt_delay_seconds}
                  onChange={(e) => formik.setFieldValue("prompt_delay_seconds", e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="Prompt-Fading Rule"
                  value={formik.values.prompt_fading_rule}
                  onChange={(e) => formik.setFieldValue("prompt_fading_rule", e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={2} label="Independent Response Definition"
                  value={formik.values.independent_response_definition}
                  onChange={(e) => formik.setFieldValue("independent_response_definition", e.target.value)} />
              </Grid>
            </Grid>
          </Card>
        )}

        {/* ── Section 4: Mastery & Data ─────────────────────────── */}
        {isSectionVisible(3) && (
          <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Mastery &amp; Data Collection</Typography>
            {/* Batch 66  - 6  -  Workflow, Measurement Type, and Baseline Notes
               moved to Treatment (Step 1) as the defining clinical
               properties of a program. Duplicates removed here. Same
               formik keys, so values entered on Treatment appear in the
               review panel. */}
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="number" label="Trials per Session"
                  inputProps={{ min: 1, max: 200 }}
                  value={formik.values.trials_per_session}
                  onChange={(e) => formik.setFieldValue("trials_per_session", e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="number" label="Minimum Sessions"
                  inputProps={{ min: 1, max: 200 }}
                  value={formik.values.minimum_sessions}
                  onChange={(e) => formik.setFieldValue("minimum_sessions", e.target.value)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" type="number" label="Mastery Threshold %"
                  inputProps={{ min: 0, max: 100, step: "0.1" }}
                  value={formik.values.mastery_threshold_percent}
                  onChange={(e) => formik.setFieldValue("mastery_threshold_percent", e.target.value)} />
              </Grid>
              {/* Batch 2  - 2 (post-report)  -  duplicate Maintenance Frequency
                 Select removed. The canonical control is now
                 MaintenanceIntervalSubControl on Section 1 (Treatment),
                 revealed conditionally when status === "maintenance".
                 The old enum-only <Select> here would have silently
                 clobbered new "N unit" values back to a legacy value
                 (weekly / biweekly / monthly / quarterly).
                 Batch 66  - 6  -  Baseline Notes moved to Treatment (Step 1)
                 alongside Objective. Duplicate here removed. */}
              <Grid item xs={12} md={4}>
                <FormControlLabel control={
                  <Switch checked={formik.values.baseline_required}
                    onChange={(e) => formik.setFieldValue("baseline_required", e.target.checked)} />
                } label="Baseline Required" />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel control={
                  <Switch checked={formik.values.automatic_progression}
                    onChange={(e) => formik.setFieldValue("automatic_progression", e.target.checked)} />
                } label="Automatic Progression" />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel control={
                  <Switch checked={formik.values.manual_override}
                    onChange={(e) => formik.setFieldValue("manual_override", e.target.checked)} />
                } label="Manual Override Allowed" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Graph Type</InputLabel>
                  <Select label="Graph Type" value={formik.values.graph_type}
                    onChange={(e) => formik.setFieldValue("graph_type", e.target.value)}>
                    <MenuItem value="">Not set</MenuItem>
                    {GRAPH_TYPES.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>
        )}

        {/* ── Section 5: Targets (inline editor) ─────────────────── */}
        {isSectionVisible(4) && (
          <TargetsWorkspace
            programId={isEdit ? Number(routeId) : null}
            onNeedDraft={async () => {
              // Transparent draft-first: saves the current form as a Draft so
              // Targets can be added without leaving the editor.
              if (isEdit) return Number(routeId);
              if (!formik.values.programName.trim()) {
                showErrorMessage("Enter a Program Name so the Draft can be saved before adding Targets.");
                return null;
              }
              try {
                const payload: any = {
                  programName: formik.values.programName.trim(),
                  status: "waiting",
                  folderId: formik.values.folderId || null,
                  category: formik.values.category || null,
                  treatment_area_id: formik.values.treatment_area_id ?? null,
                  description: formik.values.description || null,
                };
                const res = await saveProgram(payload).unwrap();
                if (res?.status && (res.data as any)?.id) {
                  const newId = Number((res.data as any).id);
                  showMessage("Draft saved so Targets can be added.");
                  navigate(`/aba-therapy/programs/edit/${newId}`, { replace: true });
                  return newId;
                }
                showErrorMessage(res?.message || "Draft save failed.");
                return null;
              } catch (e: any) {
                showErrorMessage(e?.data?.message || "Draft save failed.");
                return null;
              }
            }}
          />
        )}

        {/* ── Section 6: Students & Staff (two-panel caseload workspace) ── */}
        {isSectionVisible(5) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.25 }}>Students &amp; Staff</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: 13 }}>
              Assign ABA Students, Staff, Targets, and review dates. Save the Program first (Draft or Active) to enable assignment saving.
            </Typography>
            <StudentsStaffWorkspace
              programId={isEdit ? Number(routeId) : null}
              promptTemplates={templates.map((t: any) => ({ id: t.id, templateName: t.templateName }))}
              workflows={workflows.map((w: any) => ({ id: w.id, workflowName: w.workflowName }))}
              initialTargets={[]}
            />
          </Box>
        )}

        {/* ── Section 7: Review & Activate ─────────────────────── */}
        {isSectionVisible(6) && (
          <ReviewActivatePanel
            values={formik.values}
            templates={templates}
            workflows={workflows}
            folderLabel={flatFolders.find((f) => f.id === formik.values.folderId)?.label ?? "All Programs"}
            programId={isEdit ? Number(routeId) : null}
            onJump={(n) => setActiveSection(n)}
          />
        )}
        </fieldset>

        {/* Batch 66  - 6  -  Sticky footer with PREVIEW, CANCEL, SAVE on every
           tab. Previous/Next replaced by tab-strip navigation. Save is
           the single write action; Cancel returns to the library; Preview
           jumps to the Data Recording tab whose Review panel is the
           preview surface.
           Footer stays OUTSIDE the disabled fieldset so Cancel and Preview
           remain usable on a locked program — the user must still be able
           to leave the page and preview it. Save itself is conditionally
           absent when locked (see the {!programIsLocked && ...} guard). */}
        <Box sx={{
          position: "sticky", bottom: 0, zIndex: 3,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          mt: 2, py: 1.5, px: 2,
          borderTop: 1, borderColor: "divider",
          bgcolor: "background.paper",
          pr: { xs: 2, md: "80px" }, pb: { xs: "80px", md: 1.5 },
        }}>
          <Button
            variant="text"
            startIcon={<VisibilityIcon />}
            onClick={() => handleTabClick("data")}
            disabled={activeTab === "data"}
            sx={{ color: "text.secondary" }}
          >
            Preview
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => navigate("/aba-therapy/programs")}
            >
              Cancel
            </Button>
            {/* Save is ABSENT when the program is locked. A disabled Save
               on a form the user cannot edit is redundant chrome; the
               banner above already explains the state and provides the
               unlock action. */}
            {!programIsLocked && (
              <Button
                variant="contained"
                type="submit"
                startIcon={saving ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? "Saving" : isEdit ? "Save changes" : "Save"}
              </Button>
            )}
          </Stack>
        </Box>
      </form>

      {/* Batch 66  - 6  -  Unsaved-changes guard on tab switch. Two options
         only: Discard changes (formik.resetForm + switch), or Cancel
         (stay on current tab). NO "Save and continue"  -  partial
         persist of an unvalidated program leaves it in an
         unanswerable state. */}
      <Dialog open={pendingTab !== null} onClose={cancelSwitch} maxWidth="xs" fullWidth>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            You have unsaved edits on the {TAB_LABEL[activeTab]} tab. Switching to {pendingTab ? TAB_LABEL[pendingTab] : ""} will discard them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelSwitch}>Cancel</Button>
          <Button onClick={discardAndSwitch} color="error" variant="contained">Discard changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Label/value row using CSS grid so the value column always aligns
// under the same X within the SAME card (no fixed pixel width fighting
// the outer MUI Grid item). Rows use responsive columns: labels take
// a proportional min-slice (~35% up to a comfortable cap) so short
// values don't create a canyon of dead space in narrow half-columns.
const ReviewItem: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => {
  const isEmpty = !value || value === "-" || value === " - " || value === "Empty" || value === "Not set";
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "minmax(120px, 35%) 1fr",
      alignItems: "baseline",
      columnGap: 1.5,
      py: 0.6,
      borderBottom: "1px solid rgba(15,23,42,0.05)",
      "&:last-of-type": { borderBottom: "none" },
    }}>
      <Typography variant="caption" sx={{
        color: "text.secondary",
        fontWeight: 600,
        letterSpacing: "0.02em",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{
        minWidth: 0,
        fontWeight: isEmpty ? 500 : 600,
        color: isEmpty ? "text.disabled" : "text.primary",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {value || "—"}
      </Typography>
    </Box>
  );
};

// Batch 43  - 5  -  seven-icon segmented status row. Fastest control in either
// the client's Figma or our current tool: one tap, always visible, colour
// + icon + label all reinforce the same choice. Legacy values from older
// programs are surfaced next to the row so nothing silently changes.
const ProgramStatusPicker: React.FC<{
  value: string;
  onChange: (v: StatusKey) => void;
}> = ({ value, onChange }) => {
  const raw = String(value ?? "").toLowerCase();
  const normalised: StatusKey = (STATUS_KEYS as readonly string[]).includes(raw)
    ? (raw as StatusKey)
    : (LEGACY_STATUS_ALIASES[raw] ?? "waiting");
  const isLegacy = !!raw && !(STATUS_KEYS as readonly string[]).includes(raw);
  return (
    <Box>
      <Box
        role="radiogroup"
        aria-label="Program status"
        sx={{
          display: "inline-flex",
          border: `1px solid ${LegacyTokens.line}`,
          borderRadius: `${LegacyTokens.radius.control}px`,
          backgroundColor: LegacyTokens.surface,
          overflow: "hidden",
          flexWrap: "wrap",
        }}
      >
        {STATUS_KEYS.map((k, i) => {
          const active = normalised === k;
          // Batch 13 Part C Step 3  -  segment fill uses the B13 semantic
          // accent for THIS status, not the legacy triad-dot and not a
          // uniform primary blue. Every one of the seven states wears
          // its own colour on the Treatment tab picker so the picker
          // reads consistently with AbaStatusChip everywhere else.
          const accent = resolveB13Status(k).accent;
          return (
            <Tooltip key={k} title={PROGRAM_STATUS_LABELS[k]} arrow>
              <Box
                role="radio"
                aria-checked={active}
                tabIndex={0}
                onClick={() => onChange(k)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(k); } }}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: 0.75,
                  cursor: "pointer", userSelect: "none",
                  px: 1.5, py: 1,
                  minWidth: 96,
                  borderLeft: i === 0 ? "none" : `1px solid ${B13.line}`,
                  backgroundColor: active ? accent : "transparent",
                  color: active ? "#ffffff" : B13Ink.ink3,
                  transition: "background-color 120ms, color 120ms",
                  "&:hover": { backgroundColor: active ? accent : B13.canvas, color: active ? "#ffffff" : B13Ink.ink2 },
                  "&:focus-visible": { outline: `2px solid ${B13.primary}`, outlineOffset: -2 },
                }}
              >
                <Box sx={{ display: "inline-flex", color: active ? "#ffffff" : "inherit" }}>{STATUS_ICONS[k]}</Box>
                <Typography sx={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>
                  {PROGRAM_STATUS_LABELS[k]}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {isLegacy && (
        <Typography sx={{ fontSize: 11, color: B13Ink.ink3, mt: 0.75, display: "block" }}>
          Stored as legacy value <strong>{raw}</strong>; will save as <strong>{programStatusLabel(normalised)}</strong> next time this program is saved.
        </Typography>
      )}
    </Box>
  );
};
